// Round 1, lane C — node titles for the engine (docs/briefs/ROUND1-SPEC-2026-09-05.md section
// F.2). The engine's Branches.title must match ^[a-z0-9_-]+$ case-insensitively and be unique per
// story (vendor/ghost-ship/editor-api/src/models/branch.js:11-18); the writer's heading itself
// travels as `description`. Pure: no I/O, no state. `uniqueSlug` does not record the slug it
// returns — the caller adds it to `taken` (a lane B concern: one set per compile).
// The Unicode normalisation form named below, for the spell check:
// cspell:ignore NFKD

/** F.2: the longest title written; a longer slug is cut at the last `_` before the cap when there is one. */
export const SLUG_MAX_LENGTH = 64;

/** F.2: the slug of a heading that leaves no character behind. */
export const EMPTY_SLUG = 'scene';

// Combining marks, dropped after NFKD so that "É" becomes "e" rather than a run of underscores.
const COMBINING_MARKS = /\p{M}+/gu;
// F.2 says "every run of characters outside [a-z0-9]"; the underscore is held out of that run so
// that the function is idempotent and a heading that is ALREADY a title reads back as itself. The
// engine's own charset is [a-z0-9_-] (models/branch.js:11-18), so an underscore run is legal, and
// H.2's "nodeTitles unchanged" on the ep02 fresh compile is only true this way: 4 of its 189 titles
// carry a doubled underscore and the literal rule would fold each one to a single underscore.
// Recorded as note 7 of basil-golden-divergences/ROUND1.md, lane C.
const NON_SLUG_RUN = /[^a-z0-9_]+/g;
const EDGE_UNDERSCORES = /^_+|_+$/g;

function capped(slug: string): string {
  if (slug.length <= SLUG_MAX_LENGTH) return slug;
  const head = slug.slice(0, SLUG_MAX_LENGTH);
  const cut = head.lastIndexOf('_');
  return (cut > 0 ? head.slice(0, cut) : head).replace(EDGE_UNDERSCORES, '');
}

/**
 * F.2: NFKD-normalise, drop combining marks, lower-case, replace every run outside [a-z0-9_] with
 * `_`, trim leading and trailing `_`, `scene` when nothing is left, capped at 64 characters.
 * Idempotent: a string that is already a title comes back unchanged.
 */
export function slugTitle(heading: string): string {
  const folded = String(heading ?? '')
    .normalize('NFKD')
    .replace(COMBINING_MARKS, '')
    .toLowerCase()
    .replace(NON_SLUG_RUN, '_')
    .replace(EDGE_UNDERSCORES, '');
  const slug = capped(folded);
  return slug === '' ? EMPTY_SLUG : slug;
}

/**
 * F.2: `slug`, then `slug_2`, `slug_3`, … — the first spelling not in `taken`. Titles are unique
 * per story case-insensitively (the engine's validator is case-insensitive), so `taken` is read
 * case-insensitively; the slug itself is already lower-case.
 */
export function uniqueSlug(slug: string, taken: Set<string>): string {
  const used = new Set<string>();
  taken.forEach((title) => {
    used.add(String(title).toLowerCase());
  });
  if (!used.has(slug.toLowerCase())) return slug;
  let ordinal = 2;
  let candidate = `${slug}_${ordinal}`;
  while (used.has(candidate.toLowerCase())) {
    ordinal += 1;
    candidate = `${slug}_${ordinal}`;
  }
  return candidate;
}
